"use client";

import React, { FC } from "react";
import Link from "next/link";
import GallerySlider from "@/components/GallerySlider";
import StartRating from "@/components/StartRating";
import IconLike from "@/components/IconLike";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import { TourDetails, Slugs } from "@/data/types";
import ConvertedPrice from "@/app/[lang]/[currency]/(chrome)/checkout/ConvertedPrice";
import { setFavouriteTour } from "../app/lib/reducers/toursReducer";
import { useTranslation } from "react-i18next";
import { useCdnUrl } from "@/hooks/useCdnUrl";
import { FaRegClock } from "@react-icons/all-files/fa/FaRegClock";
import { perPersonPriceFor } from "@/utils/pricingUtils";

export interface ToursCardProps {
  className?: string;
  data?: TourDetails;
  slug?: string;
  /** Marks the LCP candidate (the very first card on the page). Only that
   *  card's first image should be eagerly preloaded — preloading 12 images
   *  competes for bandwidth and pushes the real LCP back. */
  isLcpCandidate?: boolean;
}

const DEFAULT_IMG =
  "https://guydeez-cms.s3.eu-west-3.amazonaws.com/default.png";

/**
 * Tour card used in listings (favourites, search results, etc.).
 *
 * Visually rebuilt to match the cream/burgundy theme: rounded white
 * card, hover lift, image zoom on hover, theme-aligned chip colours,
 * burgundy price tag in the footer. The two previously-empty pill
 * placeholders (city / first category) are now conditional so they
 * only render when the backend actually returned a name.
 */
const ToursCard: FC<ToursCardProps> = ({
  className = "",
  data,
  slug,
  isLcpCandidate = false,
}) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.tours.filters);
  const people = filters.people || 2;
  const connectedUser = useAppSelector((state) => state.auth.user);
  const { t } = useTranslation("guydeez");
  const toCdnUrl = useCdnUrl();

  const handleFavoriteClick = async () => {
    if (connectedUser?.uuid && data?.uuid) {
      await dispatch(setFavouriteTour(data.uuid, connectedUser?.uuid));
    }
  };

  const firstSlug = data?.slugs
    ?.flatMap((item: Slugs) => item.slug ?? [])
    .shift();
  const mainSlug = data?.slug;
  const citySlug = data?.city?.slug || slug;

  const galleryImgs =
    data?.entry?.galleries
      ?.filter((g) => g.isFeatured)
      .map((g) => toCdnUrl(g.path)) || [toCdnUrl(DEFAULT_IMG)];

  // Title-case the city label so we always render "Buenos Aires" /
  // "New York" regardless of how the backend stored the slug (favourites
  // endpoint returns lowercase, search endpoint returns translated).
  const titleCase = (s?: string | null): string => {
    if (!s) return "";
    return s
      .split(" ")
      .map((w) =>
        w
          .split("-")
          .map((p) =>
            p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : ""
          )
          .join("-")
      )
      .join(" ");
  };
  const cityName = titleCase(
    (data?.city?.nameTranslated || data?.city?.name || "").trim()
  );

  const getMinimumPriceInfo = () => {
    if (!data?.durations || data.durations.length === 0)
      return { price: 0, fromMinPeople: 0 };

    let bestPrice: number | null = null;
    let minPeopleForBest: number | null = null;

    for (const duration of data.durations) {
      if (duration.schedules && duration.schedules.length > 0) {
        for (const schedule of duration.schedules) {
          if (!schedule.activated) continue;
          for (const pricing of schedule.pricing || []) {
            if (!pricing.activated) continue;
            // Jamais pricing.price brut : une ligne "Group" porte le total du
            // groupe. On compare des tarifs par personne comparables entre eux.
            const price = perPersonPriceFor(pricing, people) ?? NaN;
            if (isNaN(price) || price <= 0) continue;
            if (
              pricing.minPeople <= people &&
              (pricing.maxPeople === null || pricing.maxPeople >= people)
            ) {
              if (bestPrice === null || price < bestPrice) {
                bestPrice = price;
                minPeopleForBest = pricing.minPeople;
              }
            }
          }
        }
      }
    }

    if (bestPrice !== null) {
      return { price: bestPrice, fromMinPeople: minPeopleForBest || 0 };
    }

    let fallback: any = null;
    for (const duration of data.durations) {
      if (duration.schedules && duration.schedules.length > 0) {
        for (const schedule of duration.schedules) {
          if (!schedule.activated) continue;
          for (const pricing of schedule.pricing || []) {
            if (!pricing.activated) continue;
            // Jamais pricing.price brut : une ligne "Group" porte le total du
            // groupe. On compare des tarifs par personne comparables entre eux.
            const price = perPersonPriceFor(pricing, pricing.minPeople || 1) ?? NaN;
            if (isNaN(price) || price <= 0) continue;
            if (!fallback || price < fallback.perPerson) {
              fallback = { row: pricing, perPerson: price };
            }
          }
        }
      }
    }

    if (fallback) {
      return {
        price: fallback.perPerson,
        fromMinPeople: fallback.row.minPeople,
      };
    }

    return { price: 0, fromMinPeople: 0 };
  };

  const { price, fromMinPeople } = getMinimumPriceInfo();
  const title = data?.entry?.data?.[0]?.title;

  // Activated category labels. The favourites endpoint sometimes ships
  // only the raw English `name` (no `nameTranslated`), so we fall back
  // down the chain so chips still render. Each is also routed through
  // an i18n fallback (`tour_category_<slug>`) for the known canonical
  // category names — same trick as the checkout sidebar.
  const i18nCategoryFallback = (englishName?: string): string | null => {
    if (!englishName) return null;
    const slug = englishName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    if (!slug) return null;
    const translated = t(`tour_category_${slug}`, {
      defaultValue: englishName,
    }) as string;
    return translated && translated !== englishName ? translated : null;
  };
  const categoryLabels: string[] = (data?.tourByCategories || [])
    .map((tc: any) => {
      const cat = tc?.tourCategory;
      if (!cat) return "";
      const translated =
        (cat.nameTranslated || "").trim() || cat.name?.trim();
      if (
        translated &&
        translated !== cat.name?.trim() &&
        !/\s[a-z]{2}_[A-Z]{2}\s*$/.test(translated)
      ) {
        return translated;
      }
      const fallback = i18nCategoryFallback(cat.name);
      return (fallback || cat.name || "").trim();
    })
    .filter(Boolean);

  return (
    <div
      className={`group relative w-full ${className}`}
      style={{
        background: "white",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid var(--g-line)",
        boxShadow: "0 4px 14px rgba(15,26,20,.06)",
        transition: "transform .25s ease, box-shadow .25s ease",
      }}
    >
      <div
        className="relative overflow-hidden"
        style={{ height: 216 }}
      >
        {/* City pill — only when the backend gave us a name. */}
        {cityName && (
          <span
            className="capitalize"
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 5,
              padding: "5px 12px",
              background: "rgba(255,255,255,.95)",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              color: "var(--g-burgundy)",
              boxShadow: "0 2px 8px rgba(0,0,0,.15)",
              pointerEvents: "none",
            }}
          >
            {cityName}
          </span>
        )}

        {/* Gallery (inner image transform handled by the slider). */}
        <GallerySlider
          uniqueID={`ToursCard_${data?.uuid}`}
          galleryImgs={galleryImgs}
          href={{ pathname: `/tour/${citySlug}/${firstSlug || mainSlug}` }}
          unoptimized={process.env.NODE_ENV === "development"}
          style={{ height: "216px" }}
          isLcpCandidate={isLcpCandidate}
        />

        {/* Favorite heart */}
        {connectedUser?.uuid && (
          <IconLike
            className="absolute right-3 top-3 z-10"
            isClicked={data?.isFavourite}
            onClick={handleFavoriteClick}
          />
        )}
      </div>

      <Link
        href={{ pathname: `/tour/${citySlug}/${firstSlug || mainSlug}` }}
        style={{ display: "block", textDecoration: "none", color: "inherit" }}
      >
        <div style={{ padding: 18 }}>
          <h3
            className="line-clamp-2"
            style={{
              color: "var(--g-burgundy)",
              fontWeight: 800,
              fontSize: 16,
              lineHeight: 1.3,
              minHeight: "2.6em",
              marginBottom: 10,
            }}
          >
            {title}
          </h3>

          {/* Rating + duration */}
          <div
            className="flex items-center"
            style={{
              gap: 10,
              fontSize: 12,
              color: "var(--g-ink-700)",
              marginBottom: 12,
              flexWrap: "wrap",
            }}
          >
            <span className="flex items-center" style={{ gap: 4 }}>
              <StartRating
                notation={data?.rating}
                totalRating={data?.totalRating}
                color={"text-primary-800"}
              />
            </span>
            {data?.miniDuration && (
              <span
                className="flex items-center"
                style={{ gap: 4, color: "var(--g-ink-500)" }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    background: "var(--g-ink-500)",
                  }}
                />
                <FaRegClock style={{ width: 12, height: 12 }} aria-hidden="true" />
                <span>
                  {data.maxiDuration === data.miniDuration
                    ? t("components_duration_available", {
                        duration: data.miniDuration,
                      })
                    : t("components_duration_from_hours_to_hours", {
                        mini: data.miniDuration,
                        maxi: data.maxiDuration,
                      })}
                </span>
              </span>
            )}
          </div>

          {/* Categories + free-cancel */}
          <div
            className="flex flex-wrap"
            style={{ gap: 6, marginBottom: 14 }}
          >
            {categoryLabels.map((label, i) => (
              <span
                key={`${label}-${i}`}
                style={{
                  padding: "3px 9px",
                  borderRadius: 99,
                  background: "rgba(31,122,69,.12)",
                  color: "var(--g-green-ada, #1F7A45)",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </span>
            ))}
            <span
              style={{
                padding: "3px 9px",
                borderRadius: 99,
                background: "rgba(224,99,90,.12)",
                color: "var(--g-coral, #E0635A)",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".04em",
                textTransform: "uppercase",
              }}
            >
              {t("free_cancellation")}
            </span>
          </div>

          {/* Price footer */}
          {price > 0 && (
            <div
              className="flex items-baseline justify-between"
              style={{
                paddingTop: 12,
                borderTop: "1px dashed var(--g-line)",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "var(--g-ink-500)",
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  fontWeight: 700,
                }}
              >
                {t("from")}
              </span>
              <span
                className="flex items-baseline"
                style={{ gap: 4 }}
              >
                <span
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: "var(--g-burgundy)",
                  }}
                >
                  <ConvertedPrice price={price} />
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--g-ink-500)",
                  }}
                >
                  /pp · {fromMinPeople}p
                </span>
              </span>
            </div>
          )}
        </div>
      </Link>

      <style jsx>{`
        .group {
          will-change: transform;
        }
        .group:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 30px rgba(15, 26, 20, 0.12) !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .group {
            transition: none !important;
          }
          .group:hover {
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ToursCard;
